/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { ICheckoutPaymentInstrumentViewProps, ICheckoutPaymentOverlayModal } from '@msdyn365-commerce-modules/checkout';
import { ICheckoutPaymentInstrumentProps as ICheckoutPaymentInstrumentExtensionProps } from '../definition-extensions/checkout-payment-instrument.ext.props.autogenerated';
import { PaymentContext as PaymentContainerContext } from '../../../modules/payment-container/context/payment-context';
import { IPaymentTenderType, PaymentContext } from '../../../utils/paymentContext';

const OverlayModal: React.FC<ICheckoutPaymentOverlayModal> = ({ modal }) => {
    return <Node {...modal} />;
};

const CheckoutPaymentInstrumentView: React.FC<ICheckoutPaymentInstrumentViewProps & ICheckoutPaymentInstrumentExtensionProps<{}>> = ({
    checkoutPaymentInstrument,
    className,
    id,
    waiting,
    alert,
    paymentInformation,
    addPaymentForm,
    overlayModal,
    config: { tenderType },
    context,
    ...restProps
}) => {
    const shouldEnableSCA: boolean = context.app.config.shouldEnableSinglePaymentAuthorizationCheckout ?? false;
    const paymentContainerContext = React.useContext(PaymentContainerContext);
    const utilsPaymentContext = React.useContext(PaymentContext);

    const paymentContext = shouldEnableSCA ? utilsPaymentContext : paymentContainerContext;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const paymentTenderTypes: IPaymentTenderType[] = [];

    React.useEffect(() => {
        tenderType?.map(tt => {
            paymentTenderTypes.push({
                tenderTypeId: tt.tenderId,
                tenderName: tt.tenderName,
                showOnNormalPartsCheckout: tt.showOnNormalPartsCheckout ?? true,
                showOnInvoiceCheckout: tt.showOnInvoiceCheckout ?? true
            });
        });
        paymentContext.registerPaymentMethod(id, paymentTenderTypes);
    }, []);

    const isIframeMarkedReady = paymentContext.iframeReadyModules[id] ?? false;

    React.useEffect(() => {
        if (isIframeMarkedReady) {
            return;
        }

        const container = containerRef.current;
        if (!container) {
            return;
        }

        let reported = false;

        const markReady = () => {
            if (!reported) {
                reported = true;
                paymentContext.reportIframeReady(id);
            }
        };

        const attachLoadListener = (iframe: HTMLIFrameElement) => {
            try {
                if (iframe.contentDocument?.readyState === 'complete') {
                    markReady();
                    return;
                }
            } catch {}
            iframe.addEventListener('load', markReady, { once: true });
        };

        const existingIframes = container.querySelectorAll('iframe');
        existingIframes.forEach(attachLoadListener);

        const observer = new MutationObserver(mutations => {
            for (const mutation of mutations) {
                for (const node of Array.from(mutation.addedNodes)) {
                    if (node instanceof HTMLIFrameElement) {
                        attachLoadListener(node);
                    }
                    if (node instanceof HTMLElement) {
                        node.querySelectorAll('iframe').forEach(attachLoadListener);
                    }
                }
            }
        });

        observer.observe(container, { childList: true, subtree: true });

        return () => {
            observer.disconnect();
        };
    }, [id, isIframeMarkedReady]);

    return (
        <div ref={containerRef}>
            <Module {...checkoutPaymentInstrument}>
                {waiting}
                {alert}
                {paymentInformation}
                {addPaymentForm}
                {overlayModal && <OverlayModal {...overlayModal} />}
            </Module>
        </div>
    );
};

export default CheckoutPaymentInstrumentView;
